import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { marketplaceService } from '@/services/marketplaceService';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import type {
  MarketplaceCategory,
  MarketplaceProduct,
  MarketplaceProductType,
  MarketplaceOffer,
} from '@/types';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Plus, Edit, Trash2, Package, FolderTree, Percent } from 'lucide-react';

interface CompanyOption {
  id: string;
  name: string;
}

export const MercadoZehPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'categorias' | 'produtos' | 'ofertas'>('categorias');

  // Categories state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MarketplaceCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    imageUrl: '',
    isActive: true,
    sortOrder: 0,
    companyId: '',
    parentId: '',
  });
  const [savingCategory, setSavingCategory] = useState(false);

  // Products state
  const [productFilters, setProductFilters] = useState({ page: 1, limit: 20, search: '', categoryId: '', companyId: '' });
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<MarketplaceProduct | null>(null);
  const [productForm, setProductForm] = useState({
    companyId: '',
    categoryId: '',
    name: '',
    slug: '',
    description: '',
    productType: 'PRODUCT' as MarketplaceProductType,
    price: '0',
    compareAtPrice: '',
    isActive: true,
    stock: '',
    sku: '',
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productImages, setProductImages] = useState<{ id: string; url: string; alt?: string | null; sortOrder: number }[]>([]);

  // Offers state
  const [offerFilters, setOfferFilters] = useState({ productId: '', companyId: '', isActive: '' });
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<MarketplaceOffer | null>(null);
  const [offerForm, setOfferForm] = useState({
    productId: '',
    title: '',
    discountPercent: '',
    startsAt: '',
    endsAt: '',
    isActive: true,
  });
  const [savingOffer, setSavingOffer] = useState(false);

  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  useQuery({
    queryKey: ['marketplace', 'companies'],
    queryFn: async () => {
      const res = await api.get<{ data: { companies: CompanyOption[] } }>('/companies');
      const list = res.data?.data?.companies ?? [];
      setCompanies(list);
      return list;
    },
  });

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['marketplace', 'categories'],
    queryFn: () => marketplaceService.listCategories(),
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['marketplace', 'products', productFilters],
    queryFn: () =>
      marketplaceService.listProducts({
        page: productFilters.page,
        limit: productFilters.limit,
        search: productFilters.search || undefined,
        categoryId: productFilters.categoryId || undefined,
        companyId: productFilters.companyId || undefined,
      }),
    enabled: activeTab === 'produtos',
  });

  const { data: offers = [], isLoading: offersLoading } = useQuery({
    queryKey: ['marketplace', 'offers', offerFilters],
    queryFn: () =>
      marketplaceService.listOffers({
        productId: offerFilters.productId || undefined,
        companyId: offerFilters.companyId || undefined,
        isActive: offerFilters.isActive === 'true' ? true : offerFilters.isActive === 'false' ? false : undefined,
      }),
    enabled: activeTab === 'ofertas',
  });

  const { data: productsForOffersData } = useQuery({
    queryKey: ['marketplace', 'products', 'for-offers'],
    queryFn: () => marketplaceService.listProducts({ limit: 500 }),
    enabled: activeTab === 'ofertas',
  });
  const productsList = productsForOffersData?.items ?? productsData?.items ?? [];

  const openNewCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      imageUrl: '',
      isActive: true,
      sortOrder: 0,
      companyId: '',
      parentId: '',
    });
    setCategoryModalOpen(true);
  };

  const openEditCategory = (cat: MarketplaceCategory) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description ?? '',
      imageUrl: cat.imageUrl ?? '',
      isActive: cat.isActive,
      sortOrder: cat.sortOrder,
      companyId: cat.companyId ?? '',
      parentId: cat.parentId ?? '',
    });
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await marketplaceService.updateCategory(editingCategory.id, {
          name: categoryForm.name,
          slug: categoryForm.slug || undefined,
          description: categoryForm.description || null,
          imageUrl: categoryForm.imageUrl || null,
          isActive: categoryForm.isActive,
          sortOrder: categoryForm.sortOrder,
          companyId: categoryForm.companyId || null,
          parentId: categoryForm.parentId || null,
        });
        toast.success('Categoria atualizada');
      } else {
        await marketplaceService.createCategory({
          name: categoryForm.name,
          slug: categoryForm.slug || undefined,
          description: categoryForm.description || null,
          imageUrl: categoryForm.imageUrl || null,
          isActive: categoryForm.isActive,
          sortOrder: categoryForm.sortOrder,
          companyId: categoryForm.companyId || null,
          parentId: categoryForm.parentId || null,
        });
        toast.success('Categoria criada');
      }
      setCategoryModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'categories'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar categoria');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await marketplaceService.deleteCategory(id);
      toast.success('Categoria excluída');
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'categories'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setProductImages([]);
    setProductForm({
      companyId: companies[0]?.id ?? '',
      categoryId: categories[0]?.id ?? '',
      name: '',
      slug: '',
      description: '',
      productType: 'PRODUCT',
      price: '0',
      compareAtPrice: '',
      isActive: true,
      stock: '',
      sku: '',
    });
    setProductModalOpen(true);
  };

  const openEditProduct = async (p: MarketplaceProduct) => {
    setEditingProduct(p);
    setProductForm({
      companyId: p.companyId,
      categoryId: p.categoryId,
      name: p.name,
      slug: p.slug,
      description: p.description ?? '',
      productType: p.productType,
      price: String(p.price),
      compareAtPrice: p.compareAtPrice != null ? String(p.compareAtPrice) : '',
      isActive: p.isActive,
      stock: p.stock != null ? String(p.stock) : '',
      sku: p.sku ?? '',
    });
    setProductImages(p.images ?? []);
    setProductModalOpen(true);
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!productForm.companyId || !productForm.categoryId) {
      toast.error('Empresa e categoria são obrigatórios');
      return;
    }
    const price = parseFloat(productForm.price);
    if (isNaN(price) || price < 0) {
      toast.error('Preço inválido');
      return;
    }
    setSavingProduct(true);
    try {
      if (editingProduct) {
        await marketplaceService.updateProduct(editingProduct.id, {
          categoryId: productForm.categoryId,
          name: productForm.name,
          slug: productForm.slug || undefined,
          description: productForm.description || null,
          productType: productForm.productType,
          price,
          compareAtPrice: productForm.compareAtPrice ? parseFloat(productForm.compareAtPrice) : null,
          isActive: productForm.isActive,
          stock: productForm.stock ? parseInt(productForm.stock, 10) : null,
          sku: productForm.sku || null,
        });
        toast.success('Produto atualizado');
      } else {
        await marketplaceService.createProduct({
          companyId: productForm.companyId,
          categoryId: productForm.categoryId,
          name: productForm.name,
          slug: productForm.slug || undefined,
          description: productForm.description || null,
          productType: productForm.productType,
          price,
          compareAtPrice: productForm.compareAtPrice ? parseFloat(productForm.compareAtPrice) : null,
          isActive: productForm.isActive,
          stock: productForm.stock ? parseInt(productForm.stock, 10) : null,
          sku: productForm.sku || null,
        });
        toast.success('Produto criado');
      }
      setProductModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'products'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar produto');
    } finally {
      setSavingProduct(false);
    }
  };

  const deleteProduct = async (id: string) => {
    if (!window.confirm('Excluir este produto?')) return;
    try {
      await marketplaceService.deleteProduct(id);
      toast.success('Produto excluído');
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'products'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const handleProductImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingProduct) return;
    e.target.value = '';
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post<{ success: boolean; data: { url: string } }>('/upload/marketplace', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (!url) throw new Error('URL não retornada');
      const added = await marketplaceService.addProductImage(editingProduct.id, { url });
      setProductImages((prev) => [...prev, { id: added.id, url, alt: added.alt ?? null, sortOrder: added.sortOrder }]);
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'products'] });
      toast.success('Imagem adicionada');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeProductImage = async (imageId: string) => {
    if (!editingProduct) return;
    try {
      await marketplaceService.removeProductImage(editingProduct.id, imageId);
      setProductImages((prev) => prev.filter((img) => img.id !== imageId));
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'products'] });
      toast.success('Imagem removida');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao remover');
    }
  };

  const openNewOffer = () => {
    setEditingOffer(null);
    setOfferForm({
      productId: productsList[0]?.id ?? '',
      title: '',
      discountPercent: '',
      startsAt: '',
      endsAt: '',
      isActive: true,
    });
    setOfferModalOpen(true);
  };

  const openEditOffer = (offer: MarketplaceOffer) => {
    setEditingOffer(offer);
    setOfferForm({
      productId: offer.productId,
      title: offer.title,
      discountPercent: offer.discountPercent != null ? String(offer.discountPercent) : '',
      startsAt: offer.startsAt ? (offer.startsAt as string).slice(0, 10) : '',
      endsAt: offer.endsAt ? (offer.endsAt as string).slice(0, 10) : '',
      isActive: offer.isActive,
    });
    setOfferModalOpen(true);
  };

  const saveOffer = async () => {
    if (!offerForm.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!editingOffer && !offerForm.productId) {
      toast.error('Selecione um produto');
      return;
    }
    setSavingOffer(true);
    try {
      if (editingOffer) {
        await marketplaceService.updateOffer(editingOffer.id, {
          title: offerForm.title,
          discountPercent: offerForm.discountPercent ? parseFloat(offerForm.discountPercent) : null,
          startsAt: offerForm.startsAt || null,
          endsAt: offerForm.endsAt || null,
          isActive: offerForm.isActive,
        });
        toast.success('Oferta atualizada');
      } else {
        await marketplaceService.createOffer({
          productId: offerForm.productId,
          title: offerForm.title,
          discountPercent: offerForm.discountPercent ? parseFloat(offerForm.discountPercent) : null,
          startsAt: offerForm.startsAt || null,
          endsAt: offerForm.endsAt || null,
          isActive: offerForm.isActive,
        });
        toast.success('Oferta criada');
      }
      setOfferModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'offers'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar oferta');
    } finally {
      setSavingOffer(false);
    }
  };

  const deleteOffer = async (id: string) => {
    if (!window.confirm('Excluir esta oferta?')) return;
    try {
      await marketplaceService.deleteOffer(id);
      toast.success('Oferta excluída');
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'offers'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  return (
    <>
      <Helmet>
        <title>Mercado do Zeh - Sistema de Cobrança</title>
      </Helmet>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Mercado do Zeh</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Categorias e produtos para oferta no aplicativo
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'categorias' | 'produtos' | 'ofertas')}>
          <TabsList>
            <TabsTrigger value="categorias">
              <FolderTree className="mr-2 h-4 w-4" />
              Categorias
            </TabsTrigger>
            <TabsTrigger value="produtos">
              <Package className="mr-2 h-4 w-4" />
              Produtos
            </TabsTrigger>
            <TabsTrigger value="ofertas">
              <Percent className="mr-2 h-4 w-4" />
              Ofertas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="categorias" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Categorias</CardTitle>
                <Button onClick={openNewCategory}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nova categoria
                </Button>
              </CardHeader>
              <CardContent>
                {categoriesLoading ? (
                  <p className="text-muted-foreground">Carregando...</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Ativo</TableHead>
                        <TableHead className="w-[100px]">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((cat) => (
                        <TableRow key={cat.id}>
                          <TableCell className="font-medium">{cat.name}</TableCell>
                          <TableCell>{cat.slug}</TableCell>
                          <TableCell>{cat.company?.name ?? '—'}</TableCell>
                          <TableCell>
                            <Badge variant={cat.isActive ? 'default' : 'secondary'}>
                              {cat.isActive ? 'Sim' : 'Não'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditCategory(cat)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => deleteCategory(cat.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {!categoriesLoading && categories.length === 0 && (
                  <p className="py-8 text-center text-muted-foreground">Nenhuma categoria cadastrada.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="produtos" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
                <CardTitle>Produtos</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Input
                    placeholder="Buscar..."
                    value={productFilters.search}
                    onChange={(e) => setProductFilters((f) => ({ ...f, search: e.target.value }))}
                    className="max-w-[200px]"
                  />
                  <Select
                    value={productFilters.categoryId}
                    onValueChange={(v) => setProductFilters((f) => ({ ...f, categoryId: v, page: 1 }))}
                    placeholder="Categoria"
                  >
                    <option value="">Todas</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                  <Select
                    value={productFilters.companyId}
                    onValueChange={(v) => setProductFilters((f) => ({ ...f, companyId: v, page: 1 }))}
                    placeholder="Empresa"
                  >
                    <option value="">Todas</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                  <Button onClick={openNewProduct}>
                    <Plus className="mr-2 h-4 w-4" />
                    Novo produto
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {productsLoading ? (
                  <p className="text-muted-foreground">Carregando...</p>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Preço</TableHead>
                          <TableHead>Ativo</TableHead>
                          <TableHead className="w-[100px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(productsData?.items ?? []).map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="font-medium">{p.name}</TableCell>
                            <TableCell>{p.category?.name ?? '—'}</TableCell>
                            <TableCell>{p.productType === 'PRODUCT' ? 'Produto' : 'Serviço'}</TableCell>
                            <TableCell>{formatCurrency(Number(p.price))}</TableCell>
                            <TableCell>
                              <Badge variant={p.isActive ? 'default' : 'secondary'}>
                                {p.isActive ? 'Sim' : 'Não'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => openEditProduct(p)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => deleteProduct(p.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {productsData?.pagination && productsData.pagination.totalPages > 1 && (
                      <div className="mt-4 flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Total: {productsData.pagination.total}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={productFilters.page <= 1}
                            onClick={() => setProductFilters((f) => ({ ...f, page: f.page - 1 }))}
                          >
                            Anterior
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={productFilters.page >= productsData.pagination.totalPages}
                            onClick={() => setProductFilters((f) => ({ ...f, page: f.page + 1 }))}
                          >
                            Próxima
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {!productsLoading && (!productsData?.items?.length) && (
                  <p className="py-8 text-center text-muted-foreground">Nenhum produto cadastrado.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ofertas" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
                <CardTitle>Ofertas</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={offerFilters.productId}
                    onValueChange={(v) => setOfferFilters((f) => ({ ...f, productId: v }))}
                    placeholder="Produto"
                  >
                    <option value="">Todos</option>
                    {productsList.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                  <Select
                    value={offerFilters.companyId}
                    onValueChange={(v) => setOfferFilters((f) => ({ ...f, companyId: v }))}
                    placeholder="Empresa"
                  >
                    <option value="">Todas</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                  <Select
                    value={offerFilters.isActive}
                    onValueChange={(v) => setOfferFilters((f) => ({ ...f, isActive: v }))}
                    placeholder="Ativo"
                  >
                    <option value="">Todos</option>
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </Select>
                  <Button onClick={openNewOffer}>
                    <Plus className="mr-2 h-4 w-4" />
                    Nova oferta
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {offersLoading ? (
                  <p className="text-muted-foreground">Carregando...</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Título</TableHead>
                        <TableHead>Desconto %</TableHead>
                        <TableHead>Início</TableHead>
                        <TableHead>Fim</TableHead>
                        <TableHead>Ativo</TableHead>
                        <TableHead className="w-[100px]">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {offers.map((o) => (
                        <TableRow key={o.id}>
                          <TableCell className="font-medium">{o.product?.name ?? '—'}</TableCell>
                          <TableCell>{o.title}</TableCell>
                          <TableCell>{o.discountPercent != null ? `${o.discountPercent}%` : '—'}</TableCell>
                          <TableCell>{o.startsAt ? formatDate(o.startsAt as string) : '—'}</TableCell>
                          <TableCell>{o.endsAt ? formatDate(o.endsAt as string) : '—'}</TableCell>
                          <TableCell>
                            <Badge variant={o.isActive ? 'default' : 'secondary'}>
                              {o.isActive ? 'Sim' : 'Não'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditOffer(o)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => deleteOffer(o.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {!offersLoading && offers.length === 0 && (
                  <p className="py-8 text-center text-muted-foreground">Nenhuma oferta cadastrada.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Modal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        title={editingCategory ? 'Editar categoria' : 'Nova categoria'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Peças"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <Input
              value={categoryForm.slug}
              onChange={(e) => setCategoryForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="Ex: pecas (deixe vazio para gerar do nome)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <Textarea
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL da imagem</label>
            <Input
              value={categoryForm.imageUrl}
              onChange={(e) => setCategoryForm((f) => ({ ...f, imageUrl: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ordem</label>
              <Input
                type="number"
                min={0}
                value={categoryForm.sortOrder}
                onChange={(e) => setCategoryForm((f) => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="cat-active"
                checked={categoryForm.isActive}
                onChange={(e) => setCategoryForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <label htmlFor="cat-active">Ativo</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Empresa (vazio = global)</label>
            <Select
              value={categoryForm.companyId}
              onValueChange={(v) => setCategoryForm((f) => ({ ...f, companyId: v }))}
              placeholder="Global"
            >
              <option value="">Global</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveCategory} disabled={savingCategory}>
              {savingCategory ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={editingProduct ? 'Editar produto' : 'Novo produto'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Empresa *</label>
            <Select
              value={productForm.companyId}
              onValueChange={(v) => setProductForm((f) => ({ ...f, companyId: v }))}
              placeholder="Selecione"
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Categoria *</label>
            <Select
              value={productForm.categoryId}
              onValueChange={(v) => setProductForm((f) => ({ ...f, categoryId: v }))}
              placeholder="Selecione"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <Input
              value={productForm.name}
              onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nome do produto ou serviço"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <Input
              value={productForm.slug}
              onChange={(e) => setProductForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="Deixe vazio para gerar do nome"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <Select
              value={productForm.productType}
              onValueChange={(v) => setProductForm((f) => ({ ...f, productType: v as MarketplaceProductType }))}
            >
              <option value="PRODUCT">Produto</option>
              <option value="SERVICE">Serviço</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Preço *</label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={productForm.price}
                onChange={(e) => setProductForm((f) => ({ ...f, price: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Preço comparativo</label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={productForm.compareAtPrice}
                onChange={(e) => setProductForm((f) => ({ ...f, compareAtPrice: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <Textarea
              value={productForm.description}
              onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Estoque (vazio = sem controle)</label>
              <Input
                type="number"
                min={0}
                value={productForm.stock}
                onChange={(e) => setProductForm((f) => ({ ...f, stock: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">SKU</label>
              <Input
                value={productForm.sku}
                onChange={(e) => setProductForm((f) => ({ ...f, sku: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="prod-active"
              checked={productForm.isActive}
              onChange={(e) => setProductForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            <label htmlFor="prod-active">Ativo</label>
          </div>
          {editingProduct && (
            <div>
              <label className="block text-sm font-medium mb-2">Imagens do produto</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {productImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.url.startsWith('http') ? img.url : `${window.location.origin}${img.url}`}
                      alt={img.alt ?? ''}
                      className="h-20 w-20 object-cover rounded border border-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="absolute top-0 right-0 h-6 w-6 p-0 opacity-90 group-hover:opacity-100"
                      onClick={() => removeProductImage(img.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <label className="inline-flex cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={uploadingImage}
                  onChange={handleProductImageUpload}
                />
                <span className="inline-flex items-center justify-center rounded-input border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
                  {uploadingImage ? 'Enviando...' : 'Upload nova imagem'}
                </span>
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setProductModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveProduct} disabled={savingProduct}>
              {savingProduct ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={offerModalOpen}
        onClose={() => setOfferModalOpen(false)}
        title={editingOffer ? 'Editar oferta' : 'Nova oferta'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Produto *</label>
            <Select
              value={offerForm.productId}
              onValueChange={(v) => setOfferForm((f) => ({ ...f, productId: v }))}
              placeholder="Selecione o produto"
              disabled={!!editingOffer}
            >
              {productsList.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            {editingOffer && (
              <p className="text-xs text-muted-foreground mt-1">Produto não pode ser alterado na edição.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <Input
              value={offerForm.title}
              onChange={(e) => setOfferForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex: Promoção de fim de ano"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Desconto (%)</label>
            <Input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={offerForm.discountPercent}
              onChange={(e) => setOfferForm((f) => ({ ...f, discountPercent: e.target.value }))}
              placeholder="Ex: 15"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Início</label>
              <Input
                type="date"
                value={offerForm.startsAt}
                onChange={(e) => setOfferForm((f) => ({ ...f, startsAt: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fim</label>
              <Input
                type="date"
                value={offerForm.endsAt}
                onChange={(e) => setOfferForm((f) => ({ ...f, endsAt: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="offer-active"
              checked={offerForm.isActive}
              onChange={(e) => setOfferForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            <label htmlFor="offer-active">Oferta ativa</label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setOfferModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveOffer} disabled={savingOffer}>
              {savingOffer ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
